export interface ResolveLocalizedResponsiveAssetOptions {
  assetMap: Record<string, unknown> | undefined;
  baseKey: string;
  isCN: boolean;
  isMobile: boolean;
}

const getAssetValue = (
  assetMap: ResolveLocalizedResponsiveAssetOptions["assetMap"],
  assetKey: string
): string | undefined => {
  const assetValue = assetMap?.[assetKey];

  return typeof assetValue === "string" && assetValue ? assetValue : undefined;
};

/**
 * 统一处理产品页图片资源选择，方便同类页面复用。
 *
 * 命名约定：
 * 1. 默认桌面图：`${baseKey}`
 * 2. 桌面语言图：`${baseKey}_cn` / `${baseKey}_en`
 * 3. 移动端通用图：`${baseKey}_mobile`
 * 4. 移动端语言图：`${baseKey}_mobile_cn` / `${baseKey}_mobile_en`
 *
 * 例子：
 * - `am_silk_glass_cp_1919_fabe_kv`
 * - `am_silk_glass_cp_1919_fabe_kv_en`
 * - `am_silk_glass_cp_1919_fabe_kv_mobile_en`
 *
 * 回退顺序：
 * - 移动端：mobile + 当前语言 -> mobile 通用 -> 桌面当前语言 -> 默认桌面图
 * - 桌面端：桌面当前语言 -> 默认桌面图
 *
 * 安全兜底：
 * - 如果某个命名对应的资源不存在，自动继续尝试下一档回退
 * - 如果所有候选资源都不存在，返回空字符串，避免读取不存在的 key 导致运行时报错
 * - 页面层面最多表现为该图片不显示，不会把整个页面打挂
 */
export const resolveLocalizedResponsiveAsset = ({
  assetMap,
  baseKey,
  isCN,
  isMobile
}: ResolveLocalizedResponsiveAssetOptions): string => {
  const localeSuffix = isCN ? "cn" : "en";
  const candidates = isMobile
    ? [
        `${baseKey}_mobile_${localeSuffix}`,
        `${baseKey}_mobile`,
        `${baseKey}_${localeSuffix}`,
        baseKey
      ]
    : [`${baseKey}_${localeSuffix}`, baseKey];

  for (const candidate of candidates) {
    const assetValue = getAssetValue(assetMap, candidate);

    if (assetValue) {
      return assetValue;
    }
  }

  return "";
};
